import type { AppRouteContext, PageContext, PageTarget } from '../types';

const MAX_TARGETS = 80;
const RUNTIME_TARGET_ATTR = 'data-agent-runtime-target';

const ROUTES: AppRouteContext[] = [
  {
    pattern: '/dashboard',
    page: 'Dashboard',
    description: 'Role-aware home page with upcoming sessions, requests, SAT entry points, and recent activity.',
    roles: ['STUDENT', 'TUTOR', 'ADMIN', 'PARENT'],
    capabilities: [
      { name: 'View sessions', description: 'Open session list and upcoming sessions.', roles: ['STUDENT', 'TUTOR', 'ADMIN'], access: 'NAVIGATE', uiTarget: 'nav-sessions' },
      { name: 'Request tutoring', description: 'Students can request a one-off tutoring session.', roles: ['STUDENT'], access: 'NAVIGATE', endpoint: '/sessions/requests', method: 'POST' },
      { name: 'Open SAT course', description: 'SAT-enabled users can open the SAT dashboard.', roles: ['STUDENT', 'TUTOR', 'ADMIN'], access: 'NAVIGATE', endpoint: '/sat' },
    ],
  },
  {
    pattern: '/admin/:tab?',
    page: 'Admin Dashboard',
    description: 'Admin console for sessions, queue, users, subjects, organizations, questions, feature flags, and AI usage.',
    roles: ['ADMIN'],
    capabilities: [
      { name: 'Manage users', description: 'Search, create, edit, delete, and test as users.', roles: ['ADMIN'], access: 'WRITE', endpoint: '/admin/users', method: 'GET/POST/PUT/DELETE' },
      { name: 'Test as user', description: 'Use the built-in admin-only impersonation flow for local/support testing.', roles: ['ADMIN'], access: 'WRITE', endpoint: '/admin/users/{id}/impersonate', method: 'POST' },
      { name: 'Manage sessions', description: 'View, create, cancel, soft-delete, restore, and complete sessions.', roles: ['ADMIN'], access: 'WRITE', endpoint: '/admin/sessions', method: 'GET/POST' },
      { name: 'Manage feature flags', description: 'Enable or disable feature flags for rollout/testing.', roles: ['ADMIN'], access: 'WRITE', endpoint: '/admin/feature-flags', method: 'GET/PUT' },
    ],
  },
  {
    pattern: '/admin/questions/editor/:id',
    page: 'Question Editor',
    description: 'Admin editor for tutoring-platform question bank items.',
    roles: ['ADMIN'],
    capabilities: [
      { name: 'Edit question', description: 'Update question content and metadata.', roles: ['ADMIN'], access: 'WRITE', endpoint: '/questions/{id}', method: 'PUT' },
      { name: 'Delete question', description: 'Remove a question from the bank.', roles: ['ADMIN'], access: 'WRITE', endpoint: '/questions/{id}', method: 'DELETE' },
    ],
  },
  {
    pattern: '/sat/:tab?',
    page: 'SAT Dashboard',
    description: 'SAT course dashboard with student journey, diagnostics, practice, question bank, templates, and profile tools depending on role.',
    roles: ['STUDENT', 'TUTOR', 'ADMIN'],
    capabilities: [
      { name: 'View SAT journey', description: 'Students see their course path, next action, plan, and benchmarks.', roles: ['STUDENT'], access: 'READ', endpoint: '/sat/me/profile/overview', method: 'GET' },
      { name: 'View SAT student directory', description: 'Tutors/admins can open SAT-enabled student profile cards.', roles: ['TUTOR', 'ADMIN'], access: 'READ', endpoint: '/sat/students/profile-cards', method: 'GET' },
      { name: 'Build practice', description: 'Tutors/admins can build practice sets from taxonomy and question inventory.', roles: ['TUTOR', 'ADMIN'], access: 'WRITE', endpoint: '/sat/practice/sets', method: 'POST' },
      { name: 'Manage SAT questions', description: 'Admins can create, edit, import, generate, and retire SAT questions.', roles: ['ADMIN'], access: 'WRITE', endpoint: '/sat/questions', method: 'GET/POST/PUT' },
      { name: 'Self enroll', description: 'Students can self-enroll in the SAT course when available.', roles: ['STUDENT'], access: 'WRITE', endpoint: '/sat/self-enroll', method: 'POST' },
    ],
  },
  {
    pattern: '/sat/practice/:id',
    page: 'SAT Practice Player',
    description: 'Student SAT practice player with questions, answer choices, review, navigation, timer, and submit flow.',
    roles: ['STUDENT'],
    capabilities: [
      { name: 'Start practice', description: 'Start or resume the assigned practice set.', roles: ['STUDENT'], access: 'WRITE', endpoint: '/sat/practice/sets/{id}/start', method: 'POST', uiTarget: 'sat-practice-start-button' },
      { name: 'Submit answer', description: 'Submit or update the selected answer for the current question.', roles: ['STUDENT'], access: 'WRITE', endpoint: '/sat/practice/sets/{id}/submit-answer', method: 'POST' },
      { name: 'Complete practice', description: 'Complete the practice set and view results.', roles: ['STUDENT'], access: 'WRITE', endpoint: '/sat/practice/sets/{id}/complete', method: 'POST', uiTarget: 'sat-question-finish-button' },
    ],
  },
  {
    pattern: '/sat/diagnostic/:id',
    page: 'SAT Diagnostic Player',
    description: 'Student SAT diagnostic player with questions, answer choices, review, navigation, timer, and submit flow.',
    roles: ['STUDENT'],
    capabilities: [
      { name: 'Start diagnostic', description: 'Start or resume the diagnostic assignment.', roles: ['STUDENT'], access: 'WRITE', endpoint: '/sat/diagnostics/assignments/{id}/start', method: 'POST', uiTarget: 'sat-practice-start-button' },
      { name: 'Submit answer', description: 'Submit or update the selected answer for the current diagnostic question.', roles: ['STUDENT'], access: 'WRITE', endpoint: '/sat/diagnostics/assignments/{id}/submit-answer', method: 'POST' },
      { name: 'Complete diagnostic', description: 'Complete the diagnostic and view results.', roles: ['STUDENT'], access: 'WRITE', endpoint: '/sat/diagnostics/assignments/{id}/complete', method: 'POST', uiTarget: 'sat-question-finish-button' },
    ],
  },
  {
    pattern: '/sat/students/:studentId/profile',
    page: 'SAT Student Profile',
    description: 'Tutor/admin SAT profile overview with mastery map, active domain, evidence, timeline, and suggested next action.',
    roles: ['TUTOR', 'ADMIN'],
    capabilities: [
      { name: 'View profile overview', description: 'Read composed SAT mastery and journey state for a student.', roles: ['TUTOR', 'ADMIN'], access: 'READ', endpoint: '/sat/students/{studentId}/profile/overview', method: 'GET' },
      { name: 'Set journey mode', description: 'Switch between tutor-led and self-serve journey mode.', roles: ['TUTOR', 'ADMIN'], access: 'WRITE', endpoint: '/sat/students/{studentId}/profile/mode', method: 'PUT' },
      { name: 'Set active domain', description: 'Change the student active SAT domain.', roles: ['TUTOR', 'ADMIN'], access: 'WRITE', endpoint: '/sat/students/{studentId}/profile/active-domain', method: 'PUT' },
    ],
  },
  {
    pattern: '/sessions',
    page: 'Sessions',
    description: 'List of current user sessions with status, details, proposals, cancellation, feedback, and meeting links.',
    roles: ['STUDENT', 'TUTOR', 'ADMIN', 'PARENT'],
    capabilities: [
      { name: 'View my sessions', description: 'List sessions visible to the current user.', roles: ['STUDENT', 'TUTOR', 'ADMIN'], access: 'READ', endpoint: '/sessions/my-sessions', method: 'GET' },
      { name: 'Approve time', description: 'Accept a proposed session time.', roles: ['STUDENT', 'TUTOR'], access: 'WRITE', endpoint: '/sessions/{id}/approve-time', method: 'POST' },
      { name: 'Cancel session', description: 'Cancel a cancellable session.', roles: ['STUDENT', 'TUTOR', 'PARENT'], access: 'WRITE', endpoint: '/sessions/{id}/cancel', method: 'POST' },
      { name: 'Submit feedback', description: 'Tutors submit feedback after a completed/no-show session.', roles: ['TUTOR'], access: 'WRITE', endpoint: '/sessions/{id}/feedback', method: 'POST' },
    ],
  },
  {
    pattern: '/calendar',
    page: 'Calendar',
    description: 'Calendar view for sessions, tutor availability, and scheduling workflows.',
    roles: ['STUDENT', 'TUTOR', 'ADMIN', 'PARENT'],
    capabilities: [
      { name: 'View calendar sessions', description: 'See sessions on the calendar.', roles: ['STUDENT', 'TUTOR', 'ADMIN', 'PARENT'], access: 'READ', endpoint: '/sessions/my-sessions', method: 'GET' },
      { name: 'Manage availability', description: 'Tutors create and update available slots.', roles: ['TUTOR'], access: 'WRITE', endpoint: '/availability/slots', method: 'POST/PUT/DELETE' },
    ],
  },
  {
    pattern: '/settings',
    page: 'Settings',
    description: 'Profile settings for theme, email, password, phone, avatar, sessions, and parent links.',
    roles: ['STUDENT', 'TUTOR', 'ADMIN', 'PARENT'],
    capabilities: [
      { name: 'Change email', description: 'Request or confirm an email change.', roles: ['STUDENT', 'TUTOR', 'ADMIN', 'PARENT'], access: 'WRITE', endpoint: '/auth/request-email-change', method: 'POST' },
      { name: 'Change password', description: 'Request and confirm password change by email code.', roles: ['STUDENT', 'TUTOR', 'ADMIN', 'PARENT'], access: 'WRITE', endpoint: '/auth/request-password-change', method: 'POST' },
      { name: 'Manage phone', description: 'Update and verify phone number.', roles: ['STUDENT', 'TUTOR', 'ADMIN', 'PARENT'], access: 'WRITE', endpoint: '/profile/phone', method: 'PUT/DELETE' },
    ],
  },
  {
    pattern: '/chats/:channelId?',
    page: 'Chats',
    description: 'Chat channels, messages, attachments, session quick actions, and group management.',
    roles: ['STUDENT', 'TUTOR', 'ADMIN', 'PARENT'],
    capabilities: [
      { name: 'View channels', description: 'List channels visible to the current user.', roles: ['STUDENT', 'TUTOR', 'ADMIN', 'PARENT'], access: 'READ', endpoint: '/chat/channels', method: 'GET' },
      { name: 'Send message', description: 'Send a chat message to a channel.', roles: ['STUDENT', 'TUTOR', 'PARENT'], access: 'WRITE', endpoint: '/chat/channels/{channelId}/messages', method: 'POST' },
      { name: 'Upload attachment', description: 'Attach a file to a chat channel.', roles: ['STUDENT', 'TUTOR', 'PARENT'], access: 'WRITE', endpoint: '/chat/channels/{channelId}/upload', method: 'POST' },
    ],
  },
  {
    pattern: '/parent',
    page: 'Parent Dashboard',
    description: 'Parent dashboard for linked students, student sessions, progress, and delegated session actions.',
    roles: ['PARENT'],
    capabilities: [
      { name: 'View linked students', description: 'List students connected to this parent.', roles: ['PARENT'], access: 'READ', endpoint: '/parent/students', method: 'GET' },
      { name: 'Act for student', description: 'Parent can perform allowed session actions on behalf of a linked student.', roles: ['PARENT'], access: 'WRITE', endpoint: '/parent/delegation', method: 'POST' },
    ],
  },
];

const INTERACTIVE_SELECTOR = [
  '[data-agent-target]',
  'button',
  'a[href]',
  'input',
  'select',
  'textarea',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="checkbox"]',
  '[role="switch"]',
].join(',');

function getElementLabel(element: Element): string | undefined {
  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel?.trim()) return ariaLabel.trim();

  const title = element.getAttribute('title');
  if (title?.trim()) return title.trim();

  const placeholder = element.getAttribute('placeholder');
  if (placeholder?.trim()) return placeholder.trim();

  const name = element.getAttribute('name');
  if (name?.trim()) return name.trim();

  const text = element.textContent?.replace(/\s+/g, ' ').trim();
  return text ? text.slice(0, 120) : undefined;
}

function normalizeTargetText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48) || 'control';
}

function roleFor(element: HTMLElement): string {
  return element.getAttribute('role') ?? element.tagName.toLowerCase();
}

function isDisabled(element: HTMLElement): boolean {
  return element.hasAttribute('disabled') || element.getAttribute('aria-disabled') === 'true';
}

function isElementVisible(element: Element, rect: DOMRect): boolean {
  const style = window.getComputedStyle(element);
  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    Number(style.opacity) !== 0 &&
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom >= 0 &&
    rect.right >= 0 &&
    rect.top <= window.innerHeight &&
    rect.left <= window.innerWidth
  );
}

export function collectPageContext(): PageContext | undefined {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return undefined;
  }

  const targets: PageTarget[] = Array.from(document.querySelectorAll<HTMLElement>(INTERACTIVE_SELECTOR))
    .flatMap((element, index) => {
      const rect = element.getBoundingClientRect();
      const label = getElementLabel(element);
      const stableTarget = element.dataset.agentTarget;
      const runtimeTarget = stableTarget ?? `runtime-${roleFor(element)}-${normalizeTargetText(label ?? element.tagName)}-${index}`;
      if (!stableTarget) {
        element.setAttribute(RUNTIME_TARGET_ATTR, runtimeTarget);
      }

      return [{
        target: runtimeTarget,
        label,
        role: roleFor(element),
        tagName: element.tagName.toLowerCase(),
        kind: stableTarget ? 'stable' : 'runtime',
        disabled: isDisabled(element),
        href: element instanceof HTMLAnchorElement ? element.getAttribute('href') ?? undefined : undefined,
        visible: isElementVisible(element, rect),
        rect: {
          x: Math.round(rect.left),
          y: Math.round(rect.top),
          width: Math.round(rect.width),
          height: Math.round(rect.height),
        },
      } satisfies PageTarget];
    })
    .filter((target) => target.visible)
    .slice(0, MAX_TARGETS);

  return {
    url: window.location.href,
    path: `${window.location.pathname}${window.location.search}${window.location.hash}`,
    title: document.title,
    route: matchRoute(window.location.pathname),
    targets,
  };
}

function matchRoute(pathname: string): AppRouteContext | undefined {
  return [...ROUTES]
    .sort((a, b) => b.pattern.split('/').length - a.pattern.split('/').length)
    .find((route) => routeMatches(route.pattern, pathname));
}

function routeMatches(pattern: string, pathname: string): boolean {
  const pathParts = pathname.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  const patternParts = pattern.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);

  for (let i = 0; i < patternParts.length; i += 1) {
    const part = patternParts[i];
    const pathPart = pathParts[i];
    if (part.endsWith('?')) {
      if (!pathPart) return i === patternParts.length - 1;
      if (part.startsWith(':')) continue;
      if (part.slice(0, -1) !== pathPart) return false;
      continue;
    }
    if (part.startsWith(':')) {
      if (!pathPart) return false;
      continue;
    }
    if (part !== pathPart) return false;
  }

  return pathParts.length === patternParts.length;
}
